import * as React from 'react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectFactEditForId, selectNewsEditForId, selectReleaseEditForId } from '../store/selectors/admin-selectors';
import {
  selectContentByDate,
  selectContentStatusByDate,
} from '../store/selectors/content-selectors';
import {
  selectDayItemsForPage,
  selectDaysStatusByPage,
} from '../store/selectors/days-selectors';
import { fetchContent } from '../store/thunks/content-thunks';
import { fetchDays } from '../store/thunks/days-thunks';
import type { GetContentV1Output } from '../lib/api/content-api-client';
import { AdminEditBadge } from '../components/AdminEditBadge';
import { AdSlot } from '../components/AdSlot';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import { type RenderableContentItem } from '../components/ContentCard';
import { EmptyResults } from '../components/EmptyResults';
import { AdminDayNav } from '../components/AdminDayNav';
import { AdminPendingForDate } from '../components/AdminPendingForDate';
import { CitationLine, type MentionedGame } from '../components/CitationLine';
import { FactEditDrawer } from '../components/FactEditDrawer';
import { NewsEditDrawer } from '../components/NewsEditDrawer';
import { PodcastPlayer } from '../components/PodcastPlayer';
import { ReleaseCard } from '../components/ReleaseCard';
import { ReleaseEditDrawer } from '../components/ReleaseEditDrawer';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { formatDisplayDateSmartYear } from '../lib/format/format-display-date';
import type { ApprovalStatus, FactEdit, NewsEdit, NewsEditSource, PurchaseLink, ReleaseEdit } from '../store/slices/admin-slice';

/**
 * Daily-page surface (task 5.5, public-website spec).
 *
 * The `<DailyPage>` container at route `/` renders today's `Daily`
 * layout per Requirement 3.1, with the Requirement 1.6 fallback
 * applied when the live `GetContentV1` call returns an empty payload
 * for today: in that case the SPA pulls the most recent seven day
 * summaries via `ListDaysWithSummariesV1`, picks the most-recent
 * non-today date that has content, and re-issues `GetContentV1`
 * against that date so the visitor still sees yesterday's (or the
 * most-recent prior day's) content rather than an empty page.
 *
 * The shared `DailyLayout` helper below renders the Requirement 3.1
 * section ordering and is reused by `<DayDetailPage>` (route
 * `/daily/:date`, task 5.5) which renders the same layout for a
 * caller-parameterized date with no 1.6 fallback applied (the user
 * explicitly asked for that date — empty is empty).
 */

type FetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Returns today's UTC date as `YYYY-MM-DD`. */
export function getTodayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Derive a YouTube thumbnail URL from a canonical YouTube video URL.
 * Accepts both `youtube.com/watch?v=<id>` and `youtu.be/<id>` shapes.
 * Returns `undefined` when the input isn't a recognizable YouTube
 * URL — the caller then falls back to an empty image string.
 *
 * `hqdefault.jpg` is the 480×360 variant YouTube guarantees for every
 * live video. Higher-resolution variants (`maxresdefault.jpg`) can 404
 * on older uploads, so we intentionally prefer the always-present
 * variant over the sharper one.
 *
 * Landed 2026-09-12 so the composer's `videoUrl` field (news items
 * whose primary media is a YouTube video) renders visually on the
 * SPA when no still image was found by the composer's image search.
 */
export function youtubeThumbnailForUrl(videoUrl: string): string | undefined {
  const id = extractYoutubeVideoId(videoUrl);
  if (id === undefined) return undefined;
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

/**
 * Extract the 11-character YouTube video id from a canonical URL.
 * Returns `undefined` when the URL isn't recognizable. Handles the
 * three common shapes: `youtube.com/watch?v=<id>[&...]`,
 * `youtu.be/<id>[?...]`, and `youtube.com/embed/<id>[?...]`.
 */
function extractYoutubeVideoId(videoUrl: string): string | undefined {
  const trimmed = videoUrl.trim();
  if (trimmed.length === 0) return undefined;
  // youtu.be/<id>
  const shortMatch = trimmed.match(/^https?:\/\/(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{11})(?:[/?#].*)?$/);
  if (shortMatch !== null) return shortMatch[1];
  // youtube.com/watch?v=<id>
  const watchMatch = trimmed.match(/^https?:\/\/(?:www\.|m\.)?youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{11})(?:[&#].*)?$/);
  if (watchMatch !== null) return watchMatch[1];
  // youtube.com/embed/<id>
  const embedMatch = trimmed.match(/^https?:\/\/(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{11})(?:[/?#].*)?$/);
  if (embedMatch !== null) return embedMatch[1];
  return undefined;
}

/**
 * Returns true when a fetched payload has no content to render —
 * empty `releases`, empty `news`, AND no `factOfTheDay` body. Used to
 * trigger the Requirement 1.6 fallback path on the Daily page.
 */
export function isContentEmpty(content: GetContentV1Output | undefined): boolean {
  if (!content) return true;
  if (content.releases.length > 0) return false;
  if (content.news.length > 0) return false;
  if (content.factOfTheDay && typeof content.factOfTheDay.body === 'string' && content.factOfTheDay.body.length > 0) {
    return false;
  }
  return true;
}

/**
 * Section heading rendered without card chrome.
 *
 * Sized as a step below the page H1 — approximately 75% of the H1's
 * font-size — so the page title remains the strongest visual anchor
 * and the section labels (Releases / News / Fact of the day /
 * Today's podcast) sit clearly underneath it. Per-item headings
 * (single-news titles, fact-of-the-day titles) render at a further
 * reduction (`h4` styling) so the hierarchy reads cleanly at three
 * levels: page → section → item.
 *
 * Margin-bottom is intentional `mb: 2` (16px) so content under any
 * section heading has breathing room rather than crowding the
 * heading. Margin-top `mt: 4` (32px) creates the same air above —
 * sections feel separated even without dividers.
 */
function SectionHeading({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <Typography
      variant="h2"
      component="h2"
      sx={{ mt: 4, mb: 2, fontSize: { xs: '1.5rem', sm: '1.875rem' }, fontWeight: 600 }}
    >
      {children}
    </Typography>
  );
}

/** Top of the Daily page — `"DVGUP for {spelled-out date}"` H1 only, no subtitle. */
function DailyHeader({ content }: { readonly content: GetContentV1Output }): JSX.Element {
  const displayDate = formatDisplayDateSmartYear(content.date);
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h1"
        component="h1"
        sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 700, mb: 1 }}
      >
        {`DVGUP for ${displayDate}`}
      </Typography>
    </Box>
  );
}

/** Pluck the first non-empty affiliate URL from an item. */
function pickPurchaseUrl(item: RenderableContentItem): string | undefined {
  const link = item.affiliateLinks?.[0];
  return typeof link?.url === 'string' && link.url.length > 0 ? link.url : undefined;
}

/**
 * Resolve the `/games/:gameId` URL for the release card's "Learn
 * more" button. Reads the encoded-body `gameId` field when present
 * and falls back to the wire `id` so the link points at a real
 * route even on legacy records.
 */
function pickLearnMoreUrl(item: RenderableContentItem): string | undefined {
  const encodedGameId = (item as { gameId?: unknown }).gameId;
  if (typeof encodedGameId === 'string' && encodedGameId.length > 0) {
    return `/games/${encodedGameId}`;
  }
  if (typeof item.id === 'string' && item.id.length > 0) {
    return `/games/${item.id}`;
  }
  return undefined;
}

/**
 * Releases section — `<ReleaseCard>` grid laid out at most 3 columns
 * wide with unlimited rows. The columns collapse to 1 on mobile (xs),
 * 2 on small (sm), 3 on medium and above (md+). One in-stack
 * `<AdSlot variant="release">` is interleaved into the grid after
 * release #1 so the ad sits inline with the cards rather than
 * outside the grid; CSS-grid auto-flow ensures the rest of the
 * releases reflow around it.
 */
function ReleasesSection({ content }: { readonly content: GetContentV1Output }): JSX.Element {
  const releases = content.releases as ReadonlyArray<RenderableContentItem>;
  const adIndex = releases.length >= 2 ? 1 : releases.length;
  const tiles: ReadonlyArray<{ kind: 'release'; item: RenderableContentItem } | { kind: 'ad' }> = [
    ...releases.slice(0, adIndex).map((item) => ({ kind: 'release' as const, item })),
    { kind: 'ad' as const },
    ...releases.slice(adIndex).map((item) => ({ kind: 'release' as const, item })),
  ];
  return (
    <Box>
      <SectionHeading>Releases</SectionHeading>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          // Always fill the full available width of the main column.
          // 1 column on xs (mobile), 2 fluid columns at sm and up
          // (each card is `1fr` of the remaining space minus the
          // gap, so cards grow with the viewport rather than
          // pinning to a fixed max width).
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
          },
        }}
      >
        {tiles.map((tile, index) => {
          if (tile.kind === 'ad') {
            return <AdSlot key={`ad-${index}`} slot={1} variant="release" />;
          }
          return (
            <AdminAwareReleaseCard
              key={tile.item.id ?? `release-${index}`}
              item={tile.item}
            />
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Wrapper around `<ReleaseCard>` that adds the admin edit badge +
 * release edit drawer. Reads any staged release edit from the
 * admin slice and folds it into the rendered card so the admin's
 * in-progress changes surface immediately.
 */
function AdminAwareReleaseCard({
  item,
}: {
  readonly item: RenderableContentItem;
}): JSX.Element {
  const recordId = typeof item.id === 'string' ? item.id : undefined;
  const adminEdit = useAppSelector((state) => selectReleaseEditForId(state, recordId));
  const baseFields = releaseItemToEditFields(item);
  const fields: ReleaseEdit = adminEdit ?? baseFields;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // When an admin edit is staged, project it back onto a render-
  // shaped item so `<ReleaseCard>` (which reads `title`/`image`/
  // `platforms`/`affiliateLinks`) picks up the edited values.
  const primaryPurchase = fields.purchaseLinks.find((l) => l.url.trim().length > 0);
  const renderedItem: RenderableContentItem = adminEdit
    ? ({
        ...item,
        title: fields.title,
        body: fields.body,
        image: fields.image,
        platforms: fields.platforms,
        affiliateLinks: fields.purchaseLinks
          .filter((l) => l.url.trim().length > 0)
          .map((l) => ({
            url: l.url,
            title: l.vendor || 'Purchase',
            asin: l.url,
            image: '',
          })),
      } as unknown as RenderableContentItem)
    : item;
  return (
    <Box sx={{ position: 'relative' }}>
      <AdminEditBadge
        onClick={() => setDrawerOpen(true)}
        label="Edit release"
        status={fields.status}
      />
      <ReleaseCard
        item={renderedItem}
        purchaseUrl={primaryPurchase?.url ?? pickPurchaseUrl(item)}
        learnMoreUrl={pickLearnMoreUrl(item)}
      />
      <ReleaseEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        recordId={recordId}
        initialValues={fields}
        hasExistingEdit={Boolean(adminEdit)}
        recordDate={extractRecordDate(item)}
        recordVersion={extractRecordVersion(item)}
      />
    </Box>
  );
}

/** Read the record's `date` field (`YYYY-MM-DD`) off the wire item. */
function extractRecordDate(item: RenderableContentItem | undefined): string | undefined {
  const raw = (item as { date?: unknown } | undefined)?.date;
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/** Read the record's `metadata.version` off the wire item. */
function extractRecordVersion(item: RenderableContentItem | undefined): number | undefined {
  const meta = (item as { metadata?: { version?: unknown } } | undefined)?.metadata;
  const raw = meta?.version;
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 ? raw : undefined;
}

/** Project a renderable release item onto the `ReleaseEdit` form shape. */
function releaseItemToEditFields(item: RenderableContentItem): ReleaseEdit {
  const titleCandidate =
    (item as { title?: string }).title ?? (item as { headline?: string }).headline ?? '';
  const imageCandidate =
    (item as { primaryImage?: string }).primaryImage ?? (item as { image?: string }).image ?? '';
  const purchaseCandidate = (item as { purchaseUrl?: string }).purchaseUrl ?? '';
  const platforms = item.platforms ?? [];
  const galleryCandidate = (item as { gallery?: ReadonlyArray<string> }).gallery ?? [];
  const videosCandidate = (item as { videos?: ReadonlyArray<string> }).videos ?? [];
  // Build the purchase link list:
  //   - prefer the encoded-body `purchaseUrl` when present (single
  //     vendor, the seed always sets this on Steam links);
  //   - fall back to the smithy `affiliateLinks` list (vendor +
  //     url pairs from the affiliate-integration follow-up).
  const purchaseLinks: PurchaseLink[] = [];
  if (purchaseCandidate) {
    purchaseLinks.push({ vendor: 'Steam', url: purchaseCandidate });
  }
  for (const link of item.affiliateLinks ?? []) {
    if (typeof link?.url === 'string' && link.url.length > 0) {
      const vendor = typeof link.title === 'string' && link.title !== 'Purchase' ? link.title : '';
      const alreadyPresent = purchaseLinks.some((p) => p.url === link.url);
      if (!alreadyPresent) purchaseLinks.push({ vendor, url: link.url });
    }
  }
  const metadata = (item as { metadata?: { status?: ApprovalStatus } }).metadata;
  const status: ApprovalStatus =
    metadata?.status === 'approved' || metadata?.status === 'rejected' || metadata?.status === 'pending'
      ? metadata.status
      : 'pending';
  return {
    kind: 'release',
    title: titleCandidate,
    body: typeof item.body === 'string' ? item.body : '',
    image: imageCandidate,
    platforms,
    gallery: galleryCandidate,
    videos: videosCandidate,
    purchaseLinks,
    status,
  };
}

/**
 * News section — flowing list of float-image news items. Each item
 * shows a smaller per-item heading (`h4`-sized) so the news titles
 * sit a step below the section heading; the image floats to the
 * left or right (alternating per item, even index = left, odd = right)
 * and the body text wraps around it. The image shapes to its
 * intrinsic contents but is capped at 50% of the article width and
 * a fixed height so it never dominates the read.
 */
function NewsSection({ content }: { readonly content: GetContentV1Output }): JSX.Element {
  const news = sortNewsByRank(content.news as ReadonlyArray<RenderableContentItem>);
  return (
    <Box>
      <SectionHeading>News</SectionHeading>
      <Stack spacing={3}>
        {news.map((item, index) => (
          <NewsItem
            key={item.id ?? index}
            item={item}
            imageFloat={index % 2 === 0 ? 'left' : 'right'}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Extract per-item `mentionedGames` from a rendered news item.
 * The composer envelope may attach a `mentionedGames: {gameId, title}[]`
 * array (round-26 addition); older records do not carry the field
 * and this helper returns an empty list.
 */
function extractMentionedGames(item: RenderableContentItem): ReadonlyArray<MentionedGame> {
  const raw = (item as { mentionedGames?: unknown }).mentionedGames;
  if (!Array.isArray(raw)) return [];
  const out: MentionedGame[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const g = entry as Record<string, unknown>;
    if (typeof g['gameId'] === 'string' && (g['gameId'] as string).length > 0
      && typeof g['title'] === 'string' && (g['title'] as string).length > 0) {
      out.push({ gameId: g['gameId'] as string, title: g['title'] as string });
    }
  }
  return out;
}

/**
 * Sort news items by envelope `rank` ascending (1 = highest impact,
 * matching the composer's ordering + admin queue sort). Items without
 * a rank fall to the tail. Stable within equal ranks.
 */
function sortNewsByRank(
  items: ReadonlyArray<RenderableContentItem>,
): ReadonlyArray<RenderableContentItem> {
  const keyed = items.map((item, i) => {
    const rawRank = (item as { rank?: unknown }).rank;
    const rank = typeof rawRank === 'number' && rawRank > 0
      ? rawRank
      : Number.MAX_SAFE_INTEGER;
    return { item, rank, i };
  });
  keyed.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return keyed.map((k) => k.item);
}

/**
 * Single news entry — heading, floated image, wrapped body, citations.
 *
 * Heading is `h4`-sized (`1rem` → `1.125rem`) so it sits below the
 * section heading in the visual hierarchy. The image floats to the
 * supplied side (`left` or `right`) and the body text wraps around
 * it. The image shapes to its intrinsic contents (`width/height:
 * auto`) but is capped at 50% of the article width and 240px tall
 * so it never dominates the read.
 *
 * Admin edit affordance: an `<AdminEditBadge>` pins to the
 * top-right corner of the entry's bounding box and is invisible to
 * non-admins. Clicking it opens `<NewsEditDrawer>` for the entry's
 * encoded-body fields. The admin slice's per-record override is
 * read on every render so saved edits surface immediately without
 * re-fetching.
 */
function NewsItem({
  item,
  imageFloat = 'left',
}: {
  readonly item: RenderableContentItem;
  readonly imageFloat?: 'left' | 'right';
}): JSX.Element {
  const recordId = typeof item.id === 'string' ? item.id : undefined;
  const adminEdit = useAppSelector((state) => selectNewsEditForId(state, recordId));
  const baseFields = newsItemToEditFields(item);
  const fields: NewsEdit = adminEdit ?? baseFields;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = fields.title;
  const image = fields.image;
  const body = fields.body;
  return (
    <Box sx={{ overflow: 'hidden', position: 'relative' }}>
      <AdminEditBadge
        onClick={() => setDrawerOpen(true)}
        label="Edit news entry"
        status={fields.status}
      />
      {title && (
        <Typography
          variant="h4"
          component="h3"
          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, mb: 1.5 }}
        >
          {title}
        </Typography>
      )}
      {image && (
        <Box
          component="img"
          src={image}
          alt={title || 'News image'}
          sx={{
            float: { xs: 'none', sm: imageFloat },
            width: { xs: '100%', sm: 'auto' },
            height: { xs: 'auto', sm: 'auto' },
            maxWidth: { xs: '100%', sm: '50%' },
            maxHeight: { sm: 240 },
            objectFit: 'contain',
            borderRadius: 1,
            ml: { sm: imageFloat === 'right' ? 2 : 0 },
            mr: { sm: imageFloat === 'left' ? 2 : 0 },
            mb: { xs: 1.5, sm: 1 },
            // Native CSS mask: fades the text-facing edge of the
            // news image to transparent so it blends into the body
            // copy rather than presenting a hard edge. When the
            // image floats LEFT, the text wraps to its RIGHT — the
            // right edge is text-facing, so the mask fades to the
            // right. Inverted when the image floats right.
            ...(imageFloat === 'left'
              ? {
                  maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
                }
              : {
                  maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 88%, transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(to left, rgba(0,0,0,1) 88%, transparent 100%)',
                }),
          }}
        />
      )}
      {body && <Typography variant="body1">{body}</Typography>}
      <CitationLine
        hashKey={recordId ?? title ?? 'news-item'}
        sources={fields.sources}
        mentionedGames={extractMentionedGames(item)}
      />
      <NewsEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        recordId={recordId}
        initialValues={fields}
        hasExistingEdit={Boolean(adminEdit)}
        recordDate={extractRecordDate(item)}
        recordVersion={extractRecordVersion(item)}
      />
    </Box>
  );
}

/** Project a renderable news item onto the `NewsEdit` form shape. */
function newsItemToEditFields(item: RenderableContentItem): NewsEdit {
  const titleCandidate =
    (item as { title?: string }).title ?? (item as { headline?: string }).headline ?? '';
  // YouTube-thumbnail fallback (2026-09-12): when the composer marks
  // an item's primary media as a video (announcement trailer or
  // gameplay reveal) but no still image was found, derive a
  // thumbnail from the video id so the news card still renders
  // visually. Falls through to empty string when the envelope has
  // neither an image nor a resolvable YouTube URL.
  const rawImage =
    (item as { primaryImage?: string }).primaryImage ?? (item as { image?: string }).image ?? '';
  const videoUrl = (item as { videoUrl?: string }).videoUrl;
  const imageCandidate = rawImage.length > 0
    ? rawImage
    : (typeof videoUrl === 'string' ? youtubeThumbnailForUrl(videoUrl) : '') ?? '';
  const sourceCandidate = (item as { source?: string }).source ?? '';
  // Round-24 link rendering fix: the api-layer's `projectRecord`
  // helper emits `sourceUrl` (camelCase) on the wire. Older seeds
  // used `source_url` (snake_case). Read both so links render
  // regardless of the seed vintage.
  const sourceUrlCandidate =
    (item as { sourceUrl?: string }).sourceUrl ??
    (item as { source_url?: string }).source_url ??
    '';
  // Round-24: news envelopes from the content-ingestion collectors
  // carry a `corroboratingSources: {source,url}[]` array with 1-3
  // additional outlets. Surface each entry as an editable citation
  // row so the SPA renders "Read more at X, Y and Z" naturally.
  const corroborating = (item as {
    corroboratingSources?: ReadonlyArray<{ source?: string; url?: string }>;
  }).corroboratingSources;
  const citationSources: NewsEditSource[] = (item.citations ?? []).map((c) => ({
    name: c.source ?? '',
    url: c.url ?? '',
  }));
  const primarySource =
    sourceCandidate.length > 0 || sourceUrlCandidate.length > 0
      ? [{ name: sourceCandidate, url: sourceUrlCandidate }]
      : [];
  const corroboratingSources: NewsEditSource[] = (corroborating ?? [])
    .filter((c) => typeof c?.url === 'string' && c.url.length > 0)
    .map((c) => ({ name: c.source ?? '', url: c.url ?? '' }));
  const sources: NewsEditSource[] =
    citationSources.length > 0
      ? citationSources
      : [...primarySource, ...corroboratingSources];
  const metadata = (item as { metadata?: { status?: ApprovalStatus } }).metadata;
  const status: ApprovalStatus =
    metadata?.status === 'approved' || metadata?.status === 'rejected' || metadata?.status === 'pending'
      ? metadata.status
      : 'pending';
  return {
    kind: 'news',
    title: titleCandidate,
    body: typeof item.body === 'string' ? item.body : '',
    image: imageCandidate,
    sources,
    status,
  };
}

/**
 * Fact-of-the-day section — single fact rendered chrome-less with a
 * smaller, **floated** image so the text wraps around it rather than
 * a full-width image stacked above the body. Works at any viewport
 * because the float collapses inside the section's stack on mobile.
 */
function FactSection({ content }: { readonly content: GetContentV1Output }): JSX.Element {
  const fact = content.factOfTheDay as RenderableContentItem;
  const recordId = typeof fact?.id === 'string' ? fact.id : undefined;
  const adminEdit = useAppSelector((state) => selectFactEditForId(state, recordId));
  const baseFields = factItemToEditFields(fact);
  const fields: FactEdit = adminEdit ?? baseFields;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const factImage = fields.image;
  const factBody = fields.body;
  const lastDigit = Number.parseInt(content.date.slice(-1), 10);
  const imageFloat: 'left' | 'right' =
    Number.isFinite(lastDigit) && lastDigit % 2 === 0 ? 'left' : 'right';
  return (
    <Box sx={{ position: 'relative' }}>
      <SectionHeading>Fact of the day</SectionHeading>
      <AdminEditBadge
        onClick={() => setDrawerOpen(true)}
        label="Edit fact of the day"
        status={fields.status}
      />
      <Box sx={{ overflow: 'hidden' }}>
        {factImage && (
          <Box
            component="img"
            src={factImage}
            alt="Fact of the day"
            sx={{
              float: { xs: 'none', sm: imageFloat },
              width: { xs: '100%', sm: 'auto' },
              height: { xs: 'auto', sm: 'auto' },
              maxWidth: { xs: '100%', sm: '50%' },
              maxHeight: { sm: 200 },
              objectFit: 'contain',
              borderRadius: 1,
              ml: { sm: imageFloat === 'right' ? 2 : 0 },
              mr: { sm: imageFloat === 'left' ? 2 : 0 },
              mb: { xs: 1.5, sm: 1 },
              // Native CSS mask: fades the image's text-facing edge
              // to transparent so it blends into the surrounding
              // body rather than presenting a hard edge. Float-LEFT
              // → text-facing edge is RIGHT → fade to right;
              // inverted when the image floats right.
              ...(imageFloat === 'left'
                ? {
                    maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
                  }
                : {
                    maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 88%, transparent 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to left, rgba(0,0,0,1) 88%, transparent 100%)',
                  }),
            }}
          />
        )}
        {factBody && <Typography variant="body1">{renderFactBodyWithEmphasis(factBody)}</Typography>}
        <CitationLine
          hashKey={recordId ?? 'fact-of-the-day'}
          sources={fields.sources}
        />
      </Box>
      <FactEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        recordId={recordId}
        initialValues={fields}
        hasExistingEdit={Boolean(adminEdit)}
        factDayOfMonth={content.date.length === 10 ? content.date.slice(5) : undefined}
        refetchDate={content.date}
        recordVersion={extractRecordVersion(fact)}
      />
    </Box>
  );
}

/** Project a renderable fact item onto the `FactEdit` form shape. */
function factItemToEditFields(item: RenderableContentItem | undefined): FactEdit {
  const imageCandidate =
    (item as { primaryImage?: string } | undefined)?.primaryImage ??
    (item as { image?: string } | undefined)?.image ??
    '';
  const citations = item?.citations ?? [];
  const sources: NewsEditSource[] = citations.map((c) => ({
    name: c.source ?? '',
    url: c.url ?? '',
  }));
  const metadata = (item as { metadata?: { status?: ApprovalStatus } } | undefined)?.metadata;
  const status: ApprovalStatus =
    metadata?.status === 'approved' || metadata?.status === 'rejected' || metadata?.status === 'pending'
      ? metadata.status
      : 'pending';
  return {
    kind: 'fact',
    body: typeof item?.body === 'string' ? item.body : '',
    image: imageCandidate,
    sources,
    status,
  };
}

/**
 * Render a fact body with proper-noun quoted spans (either curly
 * "…" or ASCII "…") replaced by italicized `<em>` runs. The raw
 * `body` string in DynamoDB retains the quote characters intact
 * (required by the future TTS/podcast generation path); this
 * function is a presentation-layer transform applied only at
 * render time.
 *
 * Landed 2026-09-13 per operator request — the curated fact JSON
 * emphasizes proper nouns like "Game Boy" and "Nintendo" with
 * curly quotes to guide TTS pronunciation, but on the SPA those
 * quote marks read as scare-quotes and look cluttered. Italicizing
 * the span (without the surrounding quotes) preserves the emphasis
 * intent without the visual noise.
 *
 * Regex captures both curly (U+201C..U+201D) and ASCII (U+0022)
 * paired quotes. Non-paired quotes (a stray `"` with no closing
 * partner within the same body) pass through unchanged so a
 * legitimate abbreviation like `12"` disc stays intact.
 */
export function renderFactBodyWithEmphasis(body: string): ReactNode {
  const pattern = /\u201C([^\u201C\u201D]+)\u201D|"([^"]+)"/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(body)) !== null) {
    if (match.index > lastIndex) {
      parts.push(body.slice(lastIndex, match.index));
    }
    const inner = match[1] ?? match[2] ?? '';
    parts.push(<em key={match.index}>{inner}</em>);
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }
  return parts.length === 0 ? body : parts;
}

/**
 * Shared Daily-page layout per Requirement 3.1 — title + hook phrase,
 * podcast player, and the three content sections (Releases, News,
 * Fact of the day). The Daily-page primary image (Requirement
 * 3.1.2) is rendered as the **release card background**, not as a
 * standalone hero, so the visual weight stays on the records the
 * viewer is browsing rather than a generic top-of-page banner. Ad
 * slots interleave per Requirement 3.1.5/.7/.9/.11; the in-stack ad
 * lives inside `<ReleasesSection>` so it matches the release-card
 * shape.
 */
export function DailyLayout({ content }: { readonly content: GetContentV1Output }): JSX.Element {
  return (
    <Stack spacing={2}>
      <DailyHeader content={content} />
      <Box>
        <SectionHeading>Today&apos;s podcast</SectionHeading>
        <PodcastPlayer />
      </Box>
      <ReleasesSection content={content} />
      <NewsSection content={content} />
      <AdSlot slot={2} variant="default" />
      <FactSection content={content} />
      <AdSlot slot={3} variant="default" />
    </Stack>
  );
}

interface DailyFallbackResult {
  readonly fallbackDate: string | null;
  readonly fallbackAttempted: boolean;
  readonly fallbackContent: GetContentV1Output | undefined;
  readonly fallbackStatus: FetchStatus;
}

/** Effect: kick off the days listing when today resolved empty. */
function useFallbackDaysFetch(todayEmpty: boolean, daysStatus: FetchStatus): void {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (todayEmpty && daysStatus === 'idle') {
      void dispatch(fetchDays({ page: 1, pageSize: 7 }));
    }
  }, [dispatch, todayEmpty, daysStatus]);
}

interface UseFallbackPickArgs {
  readonly today: string;
  readonly todayEmpty: boolean;
  readonly daysStatus: FetchStatus;
  readonly daysItems: ReturnType<typeof selectDayItemsForPage>;
  readonly fallbackAttempted: boolean;
  readonly setFallbackDate: (d: string) => void;
  readonly setFallbackAttempted: (v: boolean) => void;
}

/** Effect: once the days list resolves, pick the most-recent non-today date. */
function useFallbackPick(args: UseFallbackPickArgs): void {
  const dispatch = useAppDispatch();
  const { today, todayEmpty, daysStatus, daysItems, fallbackAttempted } = args;
  useEffect(() => {
    if (!todayEmpty || daysStatus !== 'succeeded' || fallbackAttempted) return;
    const nonToday = daysItems.find((d) => d.date !== today);
    if (nonToday) {
      args.setFallbackDate(nonToday.date);
      void dispatch(fetchContent({ date: nonToday.date }));
    }
    args.setFallbackAttempted(true);
  }, [dispatch, todayEmpty, daysStatus, daysItems, today, fallbackAttempted, args]);
}

/**
 * Encapsulates the Requirement 1.6 fallback. When today's content is
 * empty (status 'succeeded' but `isContentEmpty` true), this hook
 * dispatches `fetchDays` for the seven most recent summaries, then on
 * its success picks the most-recent non-today date and dispatches
 * `fetchContent` against it. Returns the resolved fallback content,
 * its status, and whether the fallback chain has fully resolved.
 */
function useDailyFallback(today: string, todayEmpty: boolean): DailyFallbackResult {
  const daysItems = useAppSelector((s) => selectDayItemsForPage(s, 1));
  const daysStatus = useAppSelector((s) => selectDaysStatusByPage(s, 1));
  const [fallbackDate, setFallbackDate] = useState<string | null>(null);
  const [fallbackAttempted, setFallbackAttempted] = useState(false);
  useFallbackDaysFetch(todayEmpty, daysStatus);
  useFallbackPick({
    today, todayEmpty, daysStatus, daysItems, fallbackAttempted,
    setFallbackDate, setFallbackAttempted,
  });
  const fallbackContent = useAppSelector((s) =>
    fallbackDate ? selectContentByDate(s, fallbackDate) : undefined,
  );
  const fallbackStatus = useAppSelector((s) =>
    fallbackDate ? selectContentStatusByDate(s, fallbackDate) : 'idle',
  );
  return { fallbackDate, fallbackAttempted, fallbackContent, fallbackStatus };
}

interface DailyBodyArgs {
  readonly todayStatus: FetchStatus;
  readonly todayContent: GetContentV1Output | undefined;
  readonly todayEmpty: boolean;
  readonly fallback: DailyFallbackResult;
}

function pickResolvedContent(args: DailyBodyArgs): GetContentV1Output | undefined {
  if (args.todayContent && !args.todayEmpty) return args.todayContent;
  if (args.fallback.fallbackStatus !== 'succeeded') return undefined;
  if (args.fallback.fallbackContent && !isContentEmpty(args.fallback.fallbackContent)) {
    return args.fallback.fallbackContent;
  }
  return undefined;
}

function shouldShowEmptyResults(args: DailyBodyArgs): boolean {
  if (args.todayStatus !== 'succeeded' || !args.todayEmpty) return false;
  if (!args.fallback.fallbackAttempted) return false;
  if (args.fallback.fallbackStatus === 'loading') return false;
  const fc = args.fallback.fallbackContent;
  return !fc || isContentEmpty(fc);
}

function renderDailyBody(args: DailyBodyArgs): JSX.Element {
  const showBanner = args.todayStatus === 'failed';
  const resolved = pickResolvedContent(args);
  // AdminDayNav is rendered once per page above the layout. On the
  // root Daily route we pass today's date so the picker anchors on
  // it — admins can then jump to any other date without a full page
  // refresh.
  return (
    <Stack spacing={2}>
      <SeoHead
        route="/"
        branding={brandingFixture.share}
        jsonLdType="WebSite"
      />
      <AdminDayNav currentDate={getTodayUtc()} />
      {showBanner ? <ApiUnavailableBanner /> : null}
      {resolved ? <DailyLayout content={resolved} /> : null}
      {!resolved && shouldShowEmptyResults(args) ? <EmptyResults /> : null}
      <AdminPendingForDate viewedDate={resolved?.date ?? getTodayUtc()} />
    </Stack>
  );
}

/**
 * `<DailyPage>` — root route `/` per Requirement 3.1 + Requirement
 * 1.4 / 1.5 / 1.6. Dispatches `fetchContent` for today on mount and
 * applies the 1.6 empty-day fallback. Renders `DailyLayout` for the
 * resolved content (today or fallback), `<ApiUnavailableBanner>` on
 * failure, and `<EmptyResults>` when both today and the fallback
 * resolved with no content.
 */
export default function DailyPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const today = useMemo(() => getTodayUtc(), []);
  const todayContent = useAppSelector((s) => selectContentByDate(s, today));
  const todayStatus = useAppSelector((s) => selectContentStatusByDate(s, today));
  useEffect(() => { void dispatch(fetchContent({ date: today })); }, [dispatch, today]);
  const todayEmpty = todayStatus === 'succeeded' && isContentEmpty(todayContent);
  const fallback = useDailyFallback(today, todayEmpty);
  return renderDailyBody({ todayStatus, todayContent, todayEmpty, fallback });
}
